import type { ContagemRotulo, DashboardImgr } from "@caint/data";
import type { CicloPublico, FaixaImgr } from "@caint/shared";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { mensagemAmigavel } from "../lib/erros.ts";
import { qrDataUri } from "../lib/qrcode.ts";
import { CoberturaCard } from "./CoberturaCard.tsx";
import { ComparativoPercepcao } from "./ComparativoPercepcao.tsx";
import {
  AreaLinha,
  BarrasClassificadas,
  DefinicoesNiveis,
  LegendaFaixas,
  Radar,
  Rosca,
  Velocimetro,
} from "./graficos.tsx";
import "../styles/imgr.css";

/** Cores das faixas do IMGR (Inicial→Avançado). */
const COR_FAIXA: Record<FaixaImgr, string> = {
  Inicial: "var(--danger)",
  Básico: "var(--risk-ra)",
  Intermediário: "var(--warning)",
  Aprimorado: "var(--mat-3)",
  Avançado: "var(--success)",
};
const COR_GESTOR = "var(--primary)";
const COR_LIDERADO = "var(--mat-3)";
const COR_NIVEL: Record<string, string> = {
  Estratégico: "var(--primary)",
  Tático: "var(--warning)",
  Operacional: "var(--mat-3)",
};

function pct(n: number | null): string {
  return n == null ? "—" : `${n.toFixed(1).replace(".", ",")}%`;
}

function faixaDe(v: number): FaixaImgr {
  if (v <= 20) return "Inicial";
  if (v <= 40) return "Básico";
  if (v <= 70) return "Intermediário";
  if (v <= 90) return "Aprimorado";
  return "Avançado";
}
function corPct(v: number | null): string {
  return v == null ? "var(--surface-2)" : COR_FAIXA[faixaDe(v)];
}

/** Cartão do masonry com um título de seção. */
function Cartao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="m-card pad">
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

/** Lista de contagens (cargo, área…) com barra proporcional — boa para muitos rótulos. */
function BarrasContagem({
  itens,
  total,
  cor = "var(--primary)",
}: {
  itens: readonly ContagemRotulo[];
  total: number;
  cor?: string;
}) {
  if (itens.length === 0) {
    return <p className="spec">Sem dados.</p>;
  }
  const maxN = Math.max(...itens.map((i) => i.n), 1);
  return (
    <div>
      {itens.map((i) => (
        <div key={i.rotulo} style={{ marginBottom: 7 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
            <span
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}
            >
              {i.rotulo}
            </span>
            <b>
              {i.n}
              {total > 0 ? ` · ${Math.round((i.n / total) * 100)}%` : ""}
            </b>
          </div>
          <div style={{ height: 7, borderRadius: 99, background: "var(--surface-2)", overflow: "hidden" }}>
            <div
              style={{ height: "100%", width: `${(i.n / maxN) * 100}%`, background: cor, borderRadius: 99 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Subtítulo dentro de um cartão (quando ele agrupa mais de um recorte). */
function Sub({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 700, margin: "12px 0 4px" }}>{children}</div>;
}

/** Indicador grande (números do topo). */
function Indicador({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div style={{ flex: 1, textAlign: "center", minWidth: 64 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: cor ?? "var(--text)", lineHeight: 1.1 }}>
        {valor}
      </div>
      <div className="spec">{rotulo}</div>
    </div>
  );
}

function ddmm(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${d}/${m}` : iso;
}

/** Cartão de compartilhamento: QR + link público + copiar. */
function Compartilhar({ url }: { url: string }) {
  const qr = useMemo(() => qrDataUri(url, { tamanho: 168 }), [url]);
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      setCopiado(false);
    }
  }
  return (
    <Cartao titulo="Compartilhar o formulário">
      <div style={{ textAlign: "center" }}>
        <img
          src={qr}
          alt="QR Code do formulário público"
          width={168}
          height={168}
          style={{ display: "inline-block" }}
        />
        <div className="spec" style={{ wordBreak: "break-all", marginTop: 8 }}>
          {url}
        </div>
        <button
          type="button"
          className="m-btn primary"
          style={{ marginTop: 10 }}
          onClick={() => void copiar()}
        >
          {copiado ? "✓ Link copiado" : "Copiar link"}
        </button>
      </div>
    </Cartao>
  );
}

/** Wrapper com alternador: Visão geral × comparativo Líder × Liderado. */
function Dashboard({ d, url, unidade }: { d: DashboardImgr; url: string | null; unidade?: string }) {
  const [aba, setAba] = useState<"geral" | "comparativo">("geral");
  if (d.nRespondentes === 0) {
    return <VisaoGeral d={d} url={url} />;
  }
  return (
    <div>
      <div className="imgr-tabs no-print">
        <button type="button" className={aba === "geral" ? "on" : ""} onClick={() => setAba("geral")}>
          Visão geral
        </button>
        <button
          type="button"
          className={aba === "comparativo" ? "on" : ""}
          onClick={() => setAba("comparativo")}
        >
          Líder × Liderado
        </button>
      </div>
      {aba === "comparativo" ? (
        <ComparativoPercepcao d={d} unidade={unidade} />
      ) : (
        <VisaoGeral d={d} url={url} />
      )}
    </div>
  );
}

function VisaoGeral({ d, url }: { d: DashboardImgr; url: string | null }) {
  if (d.nRespondentes === 0) {
    return (
      <div>
        <CoberturaCard d={d} />
        <div className="imgr-cols">
          <div className="m-card pad">
            <p className="spec">Ainda sem respostas neste ciclo. Compartilhe o link/QR com a equipe.</p>
          </div>
          {url && <Compartilhar url={url} />}
        </div>
      </div>
    );
  }
  const cor = d.imgr == null ? "var(--text-muted)" : COR_FAIXA[faixaDe(d.imgr)];
  const criticas = [...d.questoes]
    .filter((q) => q.pct != null)
    .sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0))
    .slice(0, 6);

  return (
    <div>
      {/* Hero: velocímetro do IMGR + perfis */}
      <div className="m-card pad imgr-hero">
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <Velocimetro valor={d.imgr} cor={cor} legenda={d.faixa ?? "—"} />
          <div style={{ flex: 1, minWidth: 150 }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>{d.faixa ?? "—"}</div>
            <div className="spec">Índice de Maturidade em Gestão de Riscos</div>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <Indicador rotulo="respostas" valor={String(d.nRespondentes)} />
            <Indicador rotulo="gestores" valor={pct(d.pctGestor)} cor={COR_GESTOR} />
            <Indicador rotulo="liderados" valor={pct(d.pctLiderado)} cor={COR_LIDERADO} />
          </div>
        </div>
      </div>

      <CoberturaCard d={d} />

      {/* Classificação por nível de maturidade (Tabela 3) — Total por componente */}
      <div className="m-card pad" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10 }}>
          Classificação por nível de maturidade (Manual IMGR · Tabela 3)
        </div>
        <LegendaFaixas />
        <BarrasClassificadas
          grupos={[
            ...d.porComponente.map((c) => ({
              rotulo: `${c.id} · ${c.nome}`,
              valores: [{ nome: "Total", valor: c.pct }],
            })),
            { rotulo: "IMGR geral", valores: [{ nome: "Total", valor: d.imgr }] },
          ]}
        />
        <DefinicoesNiveis />
      </div>

      {/* Cartões em colunas (masonry) */}
      <div className="imgr-cols">
        <Cartao titulo="Perfil dos respondentes">
          <Rosca
            itens={d.porPerfil.map((p) => ({
              rotulo: p.rotulo,
              n: p.n,
              cor: p.rotulo === "Gestor" ? COR_GESTOR : COR_LIDERADO,
            }))}
          />
        </Cartao>

        <Cartao titulo="Nível hierárquico">
          <Rosca
            itens={d.porNivel.map((x) => ({
              rotulo: x.rotulo,
              n: x.n,
              cor: COR_NIVEL[x.rotulo] ?? "var(--primary)",
            }))}
          />
        </Cartao>

        <Cartao titulo="Quem respondeu · por cargo">
          <BarrasContagem itens={d.porCargo} total={d.nRespondentes} />
        </Cartao>

        {(d.porArea.length > 0 || d.porSetor.length > 0) && (
          <Cartao titulo="Área e setor">
            {d.porArea.length > 0 && (
              <>
                <Sub>Por área</Sub>
                <BarrasContagem itens={d.porArea} total={d.nRespondentes} cor="var(--risk-ra)" />
              </>
            )}
            {d.porSetor.length > 0 && (
              <>
                <Sub>Por setor</Sub>
                <BarrasContagem itens={d.porSetor} total={d.nRespondentes} cor="var(--risk-rm)" />
              </>
            )}
          </Cartao>
        )}

        <Cartao titulo="Maturidade por componente · Gestor × Liderado">
          <Radar
            eixos={d.porComponentePerfil.map((c) => c.id)}
            series={[
              { nome: "Gestor", cor: COR_GESTOR, valores: d.porComponentePerfil.map((c) => c.pctGestor) },
              {
                nome: "Liderado",
                cor: COR_LIDERADO,
                valores: d.porComponentePerfil.map((c) => c.pctLiderado),
              },
            ]}
          />
          <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
            {d.porComponentePerfil.map((c) => (
              <div key={c.id} className="spec" style={{ display: "flex", justifyContent: "space-between" }}>
                <span>
                  <b>{c.id}</b> · {c.nome}
                </span>
                <b style={{ color: corPct(c.pctGeral) }}>{pct(c.pctGeral)}</b>
              </div>
            ))}
          </div>
        </Cartao>

        <Cartao titulo="Perguntas mais críticas (menor maturidade)">
          {criticas.map((q) => (
            <div key={q.num} style={{ marginBottom: 9 }}>
              <div style={{ display: "flex", gap: 8, fontSize: 12.5, marginBottom: 3 }}>
                <span style={{ flex: 1 }}>
                  <b>{q.componente}.</b> {q.texto}
                </span>
                <b style={{ whiteSpace: "nowrap" }}>{pct(q.pct)}</b>
              </div>
              <div
                style={{ height: 6, borderRadius: 99, background: "var(--surface-2)", overflow: "hidden" }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${q.pct ?? 0}%`,
                    background: corPct(q.pct),
                    borderRadius: 99,
                  }}
                />
              </div>
            </div>
          ))}
        </Cartao>

        {d.porDia.length > 1 && (
          <Cartao titulo="Respostas por dia">
            <AreaLinha pontos={d.porDia.map((x) => ({ rotulo: ddmm(x.rotulo), valor: x.n }))} />
          </Cartao>
        )}

        {url && <Compartilhar url={url} />}
      </div>
    </div>
  );
}

export interface PainelImgrProps {
  ciclo: CicloPublico;
  carregar: (cicloId: string) => Promise<DashboardImgr>;
}

/** Dashboard completo do IMGR de um ciclo: indicadores, recortes e compartilhamento. */
export function PainelImgr({ ciclo, carregar }: PainelImgrProps) {
  const url = ciclo.tokenPublico ? `${window.location.origin}/q/${ciclo.tokenPublico}` : null;
  const [dados, setDados] = useState<DashboardImgr | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setDados(null);
    setErro(null);
    carregar(ciclo.id)
      .then((d) => vivo && setDados(d))
      .catch((e) => vivo && setErro(mensagemAmigavel(e)));
    return () => {
      vivo = false;
    };
  }, [ciclo.id, carregar]);

  return (
    <div>
      {erro && <p style={{ color: "var(--danger)" }}>{erro}</p>}
      {!dados && !erro && <p className="spec">Carregando dashboard…</p>}
      {!dados && url && (
        <div className="imgr-cols">
          <Compartilhar url={url} />
        </div>
      )}
      {dados && <Dashboard d={dados} url={url} unidade={ciclo.unidadeNome} />}
    </div>
  );
}
